import axios from "./_axios";
import { API } from "./apiEndPoints";

/**
 * KPI API — Performance module (MDP-428 / ER-8057).
 *
 * The create-draft entry point maps onto the V2 `kpi` router's list/create
 * action (POST api/performance/v2/kpi/), which the backend implements as the
 * create-draft lifecycle operation (KPIs Catalog Rule 11 / ER-11094).
 *
 * The service layer (store/pinia/kpi.js) owns payload construction and error
 * mapping; this module is a thin HTTP shell so it stays testable in isolation.
 */

/**
 * Create a new KPI in DRAFT status.
 *
 * @param {string|number} account_id Account the KPI belongs to.
 * @param {Object} data Create-draft payload (account, name, format,
 *   favorability, operating_currency, unit_of_measure, perspective,
 *   decimal_precision, default_target_type, default_aggregation, description).
 * @returns {Promise} Axios promise resolving to the created KPI row.
 */
export const createKpiDraft = (account_id, data) => {
  let result = axios()({
    method: "post",
    url: `${API.PERFORMANCE_V2}kpi/`,
    data,
  });
  return result;
};

/** Serialises arrays as repeated keys (?status=ACTIVE&status=DRAFT), as the KPI list filter expects. */
const repeatedKeysSerializer = (params) =>
  Object.keys(params)
    .flatMap((key) => {
      const value = params[key];
      const values = Array.isArray(value) ? value : [value];
      return values
        .filter((item) => item !== undefined && item !== null && item !== "")
        .map((item) => `${encodeURIComponent(key)}=${encodeURIComponent(item)}`);
    })
    .join("&");

/**
 * List KPIs (KPIs Catalog Rule 12): paginated envelope count/next/previous/results,
 * sorted by name ascending, default visibility Active + Draft.
 *
 * @param {Object} params page, page_size, status[], has_pending_changes, format,
 *   favorability, operating_currency, search.
 * @returns {Promise} Axios promise resolving to the list envelope.
 */
export const listKpis = (params = {}) => {
  let result = axios()({
    method: "get",
    url: `${API.PERFORMANCE_V2}kpi/`,
    params,
    paramsSerializer: repeatedKeysSerializer,
  });
  return result;
};

/** KPI lifecycle operations (KPIs Catalog Rule 11). */
export const renameKpi = (id, newName) => {
  let result = axios()({
    method: "patch",
    url: `${API.PERFORMANCE_V2}kpi/${id}/rename/`,
    data: { new_name: newName },
  });
  return result;
};

export const retireKpi = (id) => {
  let result = axios()({ method: "post", url: `${API.PERFORMANCE_V2}kpi/${id}/retire/` });
  return result;
};

export const reactivateKpi = (id) => {
  let result = axios()({ method: "post", url: `${API.PERFORMANCE_V2}kpi/${id}/reactivate/` });
  return result;
};

export const archiveKpi = (id) => {
  let result = axios()({ method: "post", url: `${API.PERFORMANCE_V2}kpi/${id}/archive/` });
  return result;
};

export const unarchiveKpi = (id) => {
  let result = axios()({ method: "post", url: `${API.PERFORMANCE_V2}kpi/${id}/unarchive/` });
  return result;
};

export const unarchiveAndRenameKpi = (id, newName) => {
  let result = axios()({
    method: "post",
    url: `${API.PERFORMANCE_V2}kpi/${id}/unarchive-and-rename/`,
    data: { new_name: newName },
  });
  return result;
};

const kpiUrl = (kpi_id, operation = "") =>
  `${API.PERFORMANCE_V2}kpi/${kpi_id}/${operation ? `${operation}/` : ""}`;

/**
 * Fetch the raw KPI row (identity + draft_* staging columns).
 *
 * @param {string|number} kpi_id
 * @returns {Promise} Axios promise resolving to the raw KPI row.
 */
export const getKpi = (kpi_id) =>
  axios()({
    method: "get",
    url: kpiUrl(kpi_id),
  });

/**
 * Search the enriched KPI catalog list (KPIListSerializer rows).
 *
 * @param {Object} params Query params (search, page_size, status, ...).
 * @returns {Promise} Axios promise resolving to the paginated envelope.
 */
export const getKpiList = (params = {}) =>
  axios()({
    method: "get",
    url: `${API.PERFORMANCE_V2}kpi/`,
    params,
  });

/** edit-draft: update the editable fields of a never-activated DRAFT KPI. */
export const editKpiDraft = (kpi_id, data) =>
  axios()({ method: "patch", url: kpiUrl(kpi_id, "edit-draft"), data });

/** activate: DRAFT -> Active, materialises version 1. */
export const activateKpi = (kpi_id) => axios()({ method: "post", url: kpiUrl(kpi_id, "activate") });

/** delete-draft: hard-delete a never-activated DRAFT KPI (irreversible). */
export const deleteKpiDraft = (kpi_id) =>
  axios()({ method: "delete", url: kpiUrl(kpi_id, "delete-draft") });

/** create-pending-draft: open a pending draft on an Active KPI. */
export const createKpiPendingDraft = (kpi_id, data = {}) =>
  axios()({ method: "post", url: kpiUrl(kpi_id, "create-pending-draft"), data });

/** edit-pending-draft: update the staged values of a pending draft. */
export const editKpiPendingDraft = (kpi_id, data) =>
  axios()({ method: "patch", url: kpiUrl(kpi_id, "edit-pending-draft"), data });

/** publish-draft: publish the pending draft as the next version. */
export const publishKpiDraft = (kpi_id) =>
  axios()({ method: "post", url: kpiUrl(kpi_id, "publish-draft") });

/** discard-pending-draft: drop the pending draft. */
export const discardKpiPendingDraft = (kpi_id) =>
  axios()({ method: "post", url: kpiUrl(kpi_id, "discard-pending-draft") });
